import { useEffect, useMemo, useState } from "react";
import { AppButton, FilterBar, FilterDrawer } from "../../../components/ui";
import {
  Autocomplete,
  Box,
  CircularProgress,
  createFilterOptions,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import {
  buildActiveChips,
  clearFilterField,
  countActiveFilters,
  hasActiveFilters,
  resetFilters,
  statusFilterOptions,
} from "./filtering";
import {
  DEFAULT_SORT_RULES,
  FILTER_FIELD_LABELS,
  OVERDUE_FILTER_OPTIONS,
  PRACTICE_AREA_OPTIONS,
  SORT_DIRECTION_OPTIONS,
  SORT_FIELD_OPTIONS,
  STATUS_FILTER_NONE,
  type FilterFieldKey,
  type FilingFiltersState,
  type SortableColumn,
  type SortDirection,
  type SortRule,
} from "./types";
type FilterOptions = {
  caseName: string[];
  taskName: string[];
  practiceArea: string[];
};

type FilingReportFiltersProps = {
  filters: FilingFiltersState;
  filterOptions: FilterOptions;
  sortRules: SortRule[];
  loading: boolean;
  downloading: boolean;
  canDownload: boolean;
  onLoad: () => void;
  onApply: (next: FilingFiltersState, sortRules: SortRule[]) => void;
  onDownload: () => void;
  onClear: () => void;
};

const ALL_OPTION = "All";

const stringFilterOptions = createFilterOptions<string>({
  matchFrom: "any",
  stringify: (option) => option,
  trim: true,
  ignoreCase: true,
});

function FilterAutocomplete({
  field,
  value,
  options,
  getOptionLabel,
  onChange,
}: {
  field: FilterFieldKey;
  value: string;
  options: string[];
  getOptionLabel?: (option: string) => string;
  onChange: (value: string) => void;
}) {
  const optionsWithAll = useMemo(
    () => [ALL_OPTION, ...options.filter((o) => o !== ALL_OPTION)],
    [options]
  );

  return (
    <Autocomplete
      size="small"
      options={optionsWithAll}
      value={value || ALL_OPTION}
      onChange={(_, next) => onChange(!next || next === ALL_OPTION ? "" : next)}
      filterOptions={stringFilterOptions}
      getOptionLabel={(option) =>
        getOptionLabel && option !== ALL_OPTION ? getOptionLabel(option) : option
      }
      autoHighlight
      clearOnBlur={false}
      handleHomeEndKeys
      ListboxProps={{ style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={FILTER_FIELD_LABELS[field]}
          placeholder="Type to filter…"
        />
      )}
      fullWidth
    />
  );
}

function SortRulesEditor({
  rules,
  onChange,
}: {
  rules: SortRule[];
  onChange: (rules: SortRule[]) => void;
}) {
  const usedColumns = useMemo(() => new Set(rules.map((rule) => rule.column)), [rules]);

  const updateRule = (index: number, patch: Partial<SortRule>) => {
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  };

  const removeRule = (index: number) => {
    if (rules.length <= 1) return;
    onChange(rules.filter((_, i) => i !== index));
  };

  const addRule = () => {
    const next = SORT_FIELD_OPTIONS.find((option) => !usedColumns.has(option.value));
    if (!next) return;
    onChange([...rules, { column: next.value, direction: "asc" }]);
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
        Sort by
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        First rule has highest priority
      </Typography>
      <Stack spacing={1}>
        {rules.map((rule, index) => (
          <Stack key={`${rule.column}-${index}`} direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 14 }}>
              {index + 1}.
            </Typography>
            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
              <InputLabel id={`filing-sort-col-${index}`}>Column</InputLabel>
              <Select
                labelId={`filing-sort-col-${index}`}
                label="Column"
                value={rule.column}
                onChange={(e) =>
                  updateRule(index, { column: e.target.value as SortableColumn })
                }
              >
                {SORT_FIELD_OPTIONS.filter(
                  (option) => option.value === rule.column || !usedColumns.has(option.value)
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 118 }}>
              <InputLabel id={`filing-sort-dir-${index}`}>Dir</InputLabel>
              <Select
                labelId={`filing-sort-dir-${index}`}
                label="Dir"
                value={rule.direction}
                onChange={(e) =>
                  updateRule(index, { direction: e.target.value as SortDirection })
                }
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              size="small"
              aria-label="Remove sort rule"
              onClick={() => removeRule(index)}
              disabled={rules.length <= 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <AppButton
          size="small"
          startIcon={<AddIcon />}
          onClick={addRule}
          disabled={usedColumns.size >= SORT_FIELD_OPTIONS.length}
          sx={{ alignSelf: "flex-start" }}
        >
          Add sort
        </AppButton>
      </Stack>
    </Box>
  );
}

function DateRangeFields({
  label,
  from,
  to,
  onFromChange,
  onToChange,
}: {
  label: string;
  from: Dayjs | null;
  to: Dayjs | null;
  onFromChange: (value: Dayjs | null) => void;
  onToChange: (value: Dayjs | null) => void;
}) {
  return (
    <Box>
      <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.25}>
        <DatePicker
          label="From"
          value={from}
          onChange={onFromChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
        <DatePicker
          label="To"
          value={to}
          onChange={onToChange}
          slotProps={{ textField: { size: "small", fullWidth: true } }}
        />
      </Stack>
    </Box>
  );
}

export function FilingReportFilters({
  filters,
  filterOptions,
  sortRules,
  loading,
  downloading,
  canDownload,
  onLoad,
  onApply,
  onDownload,
  onClear,
}: FilingReportFiltersProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState<FilingFiltersState>(filters);
  const [draftSortRules, setDraftSortRules] = useState<SortRule[]>(sortRules);
  const filtersActive = hasActiveFilters(filters);
  const draftActive = hasActiveFilters(draft);
  const activeCount = countActiveFilters(filters);
  const activeChips = buildActiveChips(filters);

  useEffect(() => {
    if (drawerOpen) {
      setDraft(filters);
      setDraftSortRules(sortRules.length > 0 ? sortRules : [...DEFAULT_SORT_RULES]);
    }
  }, [drawerOpen, filters, sortRules]);

  const patchDraft = (patch: Partial<FilingFiltersState>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const clearField = (key: keyof FilingFiltersState) => {
    onApply(clearFilterField(filters, key), sortRules);
  };

  const handleApply = () => {
    onApply(draft, draftSortRules.length > 0 ? draftSortRules : [...DEFAULT_SORT_RULES]);
    setDrawerOpen(false);
  };

  const clearDraft = () => setDraft(resetFilters());

  return (
    <>
      <FilterBar
        leading={
          <>
            <AppButton variant="contained" size="small" onClick={onLoad} disabled={loading}>
              {loading ? <CircularProgress size={18} color="inherit" /> : "Load Report"}
            </AppButton>
            <AppButton
              variant="outlined"
              size="small"
              startIcon={downloading ? <CircularProgress size={16} /> : <FileDownloadIcon />}
              onClick={onDownload}
              disabled={downloading || loading || !canDownload}
              sx={{ bgcolor: "background.paper" }}
            >
              Download
            </AppButton>
          </>
        }
        activeCount={activeCount}
        filtersActive={filtersActive}
        onOpenFilters={() => setDrawerOpen(true)}
        onClear={onClear}
        clearDisabled={loading || !filtersActive}
        chips={activeChips.map((chip) => ({
          key: String(chip.key),
          label: chip.label,
          onDelete: () => clearField(chip.key),
        }))}
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title="Filters & sort"
        subtitle="Apply to update the table"
        activeCount={activeCount}
        actions={
          <>
            <AppButton
              variant="outlined"
              color="warning"
              fullWidth
              startIcon={<FilterAltOffIcon />}
              onClick={clearDraft}
              disabled={!draftActive}
            >
              Clear filters
            </AppButton>
            <AppButton variant="contained" fullWidth onClick={handleApply} disabled={loading}>
              Apply
            </AppButton>
          </>
        }
      >
        <Stack spacing={1.75}>
          <FilterAutocomplete
            field="caseName"
            value={draft.caseName}
            options={filterOptions.caseName}
            onChange={(value) => patchDraft({ caseName: value })}
          />
          <FilterAutocomplete
            field="taskName"
            value={draft.taskName}
            options={filterOptions.taskName}
            onChange={(value) => patchDraft({ taskName: value })}
          />
          <FilterAutocomplete
            field="practiceArea"
            value={draft.practiceArea}
            options={[
              ...PRACTICE_AREA_OPTIONS.filter((o) => filterOptions.practiceArea.includes(o)),
              ...filterOptions.practiceArea.filter(
                (o) => !(PRACTICE_AREA_OPTIONS as readonly string[]).includes(o)
              ),
            ]}
            onChange={(value) => patchDraft({ practiceArea: value })}
          />
          <FilterAutocomplete
            field="status"
            value={draft.status}
            options={statusFilterOptions()}
            getOptionLabel={(option) =>
              option === STATUS_FILTER_NONE ? "No status" : option
            }
            onChange={(value) => patchDraft({ status: value })}
          />
          <FilterAutocomplete
            field="overdue"
            value={draft.overdue}
            options={[...OVERDUE_FILTER_OPTIONS]}
            onChange={(value) => patchDraft({ overdue: value })}
          />
          <TextField
            size="small"
            label={FILTER_FIELD_LABELS.searchTerm}
            placeholder="Search…"
            value={draft.searchTerm}
            onChange={(e) => patchDraft({ searchTerm: e.target.value })}
            fullWidth
          />
          <DateRangeFields
            label="Created At"
            from={draft.createdFrom}
            to={draft.createdTo}
            onFromChange={(value) => patchDraft({ createdFrom: value })}
            onToChange={(value) => patchDraft({ createdTo: value })}
          />
          <DateRangeFields
            label="Due Date"
            from={draft.dueFrom}
            to={draft.dueTo}
            onFromChange={(value) => patchDraft({ dueFrom: value })}
            onToChange={(value) => patchDraft({ dueTo: value })}
          />
          <SortRulesEditor rules={draftSortRules} onChange={setDraftSortRules} />
        </Stack>
      </FilterDrawer>
    </>
  );
}
